import {
  type NativeId,
  getNativeNodeByNativeId,
} from '@packages/systems/core/utils/NativeIdUtils';
import {Component, Expressions} from '@packages/systems/wfdl';
import {
  getOutermostSymbolInstance,
  getPageComponentFromComponentMap,
  isUserComponentInst,
  getBody,
  findElementDescendant,
  getComponentByOrigin,
  deriveNativeIdFromPath,
  getPageComponentRender,
  findAllDescendantsFromExpression,
  isUserComponent,
  getComponentInstRootElement,
  getSlots,
  getDescendants,
  isUserComponentRoot,
  isComponent,
  findFurthestAncestor,
  getTargetElementOrInstance,
  isRichTextDescendant,
  isComponentOnlyPageSlotDescendant,
  getSlotOverrideFieldId,
  getSlotDefaultElements,
  isEmptyFormPlaceholder,
} from '@packages/utilities/platform';
import {
  isComponentOnlyDropTarget,
  isRichText,
} from '@packages/utilities/elementTypeUtils';
import {getPluginName, getName} from '@packages/systems/wfdl-Qualified';
import {selectDesignerAbility} from '@packages/systems/designer-abilities';
import {EElement, ExpressionElement} from '@packages/systems/wfdl-ast';
import {createSelector} from 'reselect';

import {type DesignerStateType} from '@packages/systems/core/records/DesignerState';
import {singleMemo, weakMemo} from '@packages/utilities/memo';
import {emptyArray} from '@packages/utilities/fp/utils';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {getFeatureFlagOutsideOfReact} from '@packages/systems/feature-config/client';
export {
  type AutoSelectResult,
  autoSelectPageSlotForDragEvent,
} from './autoSelectPageSlotForDragEvent';

export type PageBuildModeValidationContext = {
  isInPageBuildMode: boolean;
  isInCampaignBuildMode: boolean;
  canAccessDesignSiteMode: boolean;
};

// When realtime is off, a component that has not been saved to site data cannot be used in page
// build mode due to our inability to sync styles & IX in that mode. This only applies to WS
// Library Components that have not been used yet. Once the WS Library components have been used,
// they are saved to site data and can be used in page build mode regardless of the realtime flag.
export const COMPONENT_NOT_SAVED_TO_SITE_DATA =
  'COMPONENT_NOT_SAVED_TO_SITE_DATA' as const;

// A stale component is a component that has been edited by the design leader so the
// page builder's (markete's) local site has an out of date version of the component.
export const COMPONENT_IS_STALE = 'COMPONENT_IS_STALE' as const;

export type DisabledReason =
  | typeof COMPONENT_NOT_SAVED_TO_SITE_DATA
  | typeof COMPONENT_IS_STALE;

export const DROP_TARGET_CLASS = 'wf-drop-target' as const;
export const DROP_TARGET_SINGLE_CLASS = 'wf-drop-target-single' as const;
export const COMPONENT_SLOT_CLASS = 'wf-component-slot' as const;

export type DropTargetClass =
  | typeof DROP_TARGET_CLASS
  | typeof DROP_TARGET_SINGLE_CLASS;

export type PageBuildModeComponentAvailability =
  | {
      canUseComponentInPageBuildMode: true;
    }
  | {
      canUseComponentInPageBuildMode: false;
      disabledReason: DisabledReason;
    };

export const selectStaleComponents = ({
  DesignerStore,
}: {
  DesignerStore: DesignerStateType;
}) => {
  return DesignerStore.staleComponents;
};

/**
 * Checks whether the component referenced by the given native id path is
 * a descendant of a Page Slot.
 * Note: There is a similar function that accepts an element in `platform` package: isComponentOnlyPageSlotDescendant
 * @param nativeIdPath - the path to the element to check
 * @param componentMap
 * @returns boolean
 */
export const getIsComponentOnlyPageSlotDescendantFromPath = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap
): boolean => {
  const outermostSymbolInstance = getOutermostSymbolInstance(
    nativeIdPath,
    componentMap
  );
  const pageComponent = getPageComponentFromComponentMap(componentMap);
  const pageRender = pageComponent && Component.getRender(pageComponent);

  if (
    outermostSymbolInstance &&
    pageRender &&
    isComponentOnlyPageSlotDescendant(pageRender, outermostSymbolInstance)
  ) {
    return true;
  } else {
    return false;
  }
};

/**
 * Checks whether the element referenced by the given native id path is
 * a descendant of a Rich Text element.
 * Note: There is a similar function that accepts an element in `platform` package: isRichTextDescendant.
 * @param nativeIdPath - the path to the element to check
 * @param componentMap
 * @returns boolean
 */
export const getIsRichTextDescendantFromPath = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap
): boolean => {
  const outermostSymbolInstance = getOutermostSymbolInstance(
    nativeIdPath,
    componentMap
  );
  const pageComponent = getPageComponentFromComponentMap(componentMap);
  const pageRender = pageComponent && Component.getRender(pageComponent);

  if (!pageRender) {
    return false;
  }

  // If the element is inside a component instance
  if (outermostSymbolInstance) {
    return isRichTextDescendant(pageRender, outermostSymbolInstance);
  }

  // If the element is directly on the page
  const element = getTargetElementOrInstance(nativeIdPath, componentMap);
  if (!element) {
    return false;
  }

  return isRichTextDescendant(pageRender, element);
};

/**
 * If a component instance has not be added to the page yet, it will have a nativeIdPath, but it will
 * not be a descendant of a page slot yet. We should allow the user to add it to the page.
 * @param nativeId
 * @returns boolean
 */
const getIsUnattachedComponentInstance = (nativeId: NativeId): boolean => {
  // If the component does not exist in the DOM yet, it is unattached & therefore can be picked up
  const elementExists = getNativeNodeByNativeId(nativeId);

  return !elementExists;
};

/**
 * Determines whether the component at the given native id path is moveable in page build mode
 * @param nativeIdPath
 * @param componentMap
 * @returns boolean
 */
export const isMoveableInPageBuildMode = ({
  nativeIdPath,
  componentMap,
  focusedSymbolNativeIdPath,
}: {
  nativeIdPath: NativeId[] | null;
  componentMap: Component.ComponentMap;
  focusedSymbolNativeIdPath: NativeId[];
}): boolean => {
  if (!nativeIdPath) {
    return false;
  }

  const nativeId = deriveNativeIdFromPath(
    nativeIdPath,
    focusedSymbolNativeIdPath
  );

  // A page-slot descendant is only movable in build mode when the dragged
  // element is ITSELF a component instance. getIsComponentOnlyPageSlotDescendantFromPath
  // resolves the path up to its OUTERMOST enclosing instance, so a plain element
  // nested inside a slotted component (e.g. an image inside a Hero) would
  // otherwise report as movable — the drag then starts and later fails build-mode
  // placement validation, surfacing a "must be placed in page slots" toast on
  // what is usually an accidental micro-drag. Reject a resolved non-component
  // element up front so pickup fails silently; fall back to the existing check
  // when the element is a component instance or the path can't be resolved.
  const targetElement = getTargetElementOrInstance(nativeIdPath, componentMap);
  const isResolvedNonComponentElement =
    targetElement != null && !isUserComponentInst(componentMap, targetElement);
  const isComponentPageSlotDescendant =
    !isResolvedNonComponentElement &&
    getIsComponentOnlyPageSlotDescendantFromPath(nativeIdPath, componentMap);
  const isRichTextChild = getIsRichTextDescendantFromPath(
    nativeIdPath,
    componentMap
  );
  const isUnattachedComponentInstance = Boolean(
    nativeId && getIsUnattachedComponentInstance(nativeId)
  );

  return (
    isComponentPageSlotDescendant ||
    isRichTextChild ||
    isUnattachedComponentInstance
  );
};

/**
 * Checks if a user in page build mode can remove the element.
 *
 * Returns true if the element is a component instance and
 * is inside a page slot.
 *
 * If canAddComponentsInStaticRichText is true,
 * also returns true if the element is a component instance and
 * is inside a rich text element.
 */
export function canRemoveElementInPageBuildMode(
  element: ExpressionElement | undefined | null,
  componentMap: Component.ComponentMap,
  canAddComponentsInStaticRichText: boolean
): boolean {
  const pageComponent = getPageComponentFromComponentMap(componentMap);
  const pageRender = pageComponent && Component.getRender(pageComponent);

  const canRemoveElement =
    element &&
    pageRender &&
    isUserComponentInst(componentMap, element) &&
    (isComponentOnlyPageSlotDescendant(pageRender, element) ||
      (canAddComponentsInStaticRichText &&
        Boolean(findFurthestAncestor(pageRender, element, isRichText))));

  return Boolean(canRemoveElement);
}

/**
 * Gets the Element of the first page slot on the page, or undefined if there are none on the page
 * @param componentMap
 * @returns the Element of the first page slot on the page, or undefined if there are none on the page
 */
export const getFirstPageSlotElementOnPage = (
  componentMap: Component.ComponentMap
): ExpressionElement | undefined => {
  const body = getBody(componentMap);
  if (!body) return undefined;

  const firstPageSlotElementOnPage = findElementDescendant(
    body,
    isComponentOnlyDropTarget
  );

  return firstPageSlotElementOnPage;
};

/**
 * In page build mode, user components and Basic components can always be used.
 *
 * Workspace Library components can be used once they have a local copy in site data, or
 * immediately when realtime is enabled.
 * @param element
 * @param components
 * @returns boolean
 */
export const canUseComponentInPageBuildMode = ({
  element,
  components,
}: {
  element: ExpressionElement;
  components: Component.ComponentMap;
}) => {
  // The expression is a user component, so it can be used in page build mode
  if (
    isUserComponentInst(components, element) ||
    isUserComponentRoot(components, element)
  ) {
    return true;
  }

  // Basic::LineBreak components should always be allowed. These are created by the system when
  // a user adds a line break to some text.
  // There is a known issue where Basic::LineBreak components are treated as library components.
  // To be safe, we allow all Basic components.
  // See https://webflow.atlassian.net/browse/SCALE-959 for more details.
  if (getPluginName(element.type) === 'Basic') {
    return true;
  }

  // If it is a component, but not a user component and not a Basic::LineBreak component, then it must be a plugin component like a WS Library.
  // In order for these components to be used in build mode, they must be a local component.
  // In such a scenario, getComponentByOrigin can be called with element.type instead of origin because
  // it will either find the local copy for the plugin component, meaning it can be used by page builders,
  // or not, meaning it can't be used!
  // We do not pass the origin param here because for the case where we are adding a component from the
  // Components Panel to the page, the component does not yet exist in the component map & therefore, no origin
  // is returned. This is why we pass the element.type instead.

  const localComponent = getComponentByOrigin(element.type, components);

  const canBeUsedInPageBuildMode = Boolean(localComponent?.component);
  const isRealtimeEnabled = getFeatureFlagOutsideOfReact(
    FEATURE_FLAGS.REALTIME
  );

  return canBeUsedInPageBuildMode || isRealtimeEnabled;
};

/**
 * Checks if a component and all of its component descendants can be used in page build mode
 */
export const canUseComponentAndDescendantsInPageBuildMode = ({
  components,
  element,
}: {
  components: Component.ComponentMap;
  element: ExpressionElement;
}) => {
  const componentElements = findAllDescendantsFromExpression(
    EElement(element),
    (e) => {
      return isComponent(components)(e);
    }
  );

  if (componentElements.length === 0) {
    return false;
  }

  return componentElements.every((e) => {
    return canUseComponentInPageBuildMode({
      element: e,
      components,
    });
  });
};

/**
 * Returns a Page Build mode validation context used in page validation
 * @param state
 */
export const selectPageBuildModeValidationContext = createSelector(
  selectDesignerAbility('isInPageBuildMode'),
  selectDesignerAbility('isInCampaignBuildMode'),
  selectDesignerAbility('canAccessDesignSiteMode'),
  (isInPageBuildMode, isInCampaignBuildMode, canAccessDesignSiteMode) => ({
    isInPageBuildMode,
    isInCampaignBuildMode,
    canAccessDesignSiteMode,
  })
);

const getAllPageSlots = weakMemo(
  (components: Component.ComponentMap): ExpressionElement[] => {
    const pageRender = getPageComponentRender(components);
    if (!pageRender) {
      // return emptyArray instead of [] so it does not return a new empty array every time it is called.
      // this helps with memoization that depends on the result of this call
      return emptyArray;
    }
    return findAllDescendantsFromExpression(
      pageRender,
      isComponentOnlyDropTarget
    );
  }
);

const isUserComponentFromElement = (element: ExpressionElement): boolean =>
  isUserComponent(element.type);

// Instances in the content that aren't nested inside another of its instances.
const getOutermostInstances = (
  content: ExpressionElement[]
): ExpressionElement[] => {
  const instances = content.flatMap((element) =>
    findAllDescendantsFromExpression(
      EElement(element),
      isUserComponentFromElement
    )
  );
  const nestedIds = new Set(
    instances.flatMap((instance) =>
      findAllDescendantsFromExpression(
        EElement(instance),
        isUserComponentFromElement
      )
        .filter((nested) => nested.id !== instance.id)
        .map((nested) => nested.id)
    )
  );
  return instances.filter((instance) => !nestedIds.has(instance.id));
};

const addInstanceSlotsToMap = (
  components: Component.ComponentMap,
  componentInstance: ExpressionElement,
  map: {[key: string]: boolean},
  projectingPath: string[] = []
) => {
  const rootElement =
    getComponentInstRootElement(components)(componentInstance);
  const slots = rootElement && getSlots(rootElement);
  // if the component contains slots, add each to the map
  if (!slots) return;

  const instancePath = [...projectingPath, componentInstance.id];
  for (const slot of slots) {
    map[[...instancePath, slot.id].join('+')] = true;

    // An inheriting Slot shows its default content, which is never in the page
    // render. Default ids are shared by every instance that projects them, so
    // its keys carry the projecting path to stay unique per placement.
    const slotFieldId = getSlotOverrideFieldId(slot);
    if (!slotFieldId) continue;
    const override = Expressions.getIn(
      Expressions.getElementData(componentInstance),
      [{in: 'Record', at: slotFieldId}]
    );
    // An authored override on the page is reached by the page walk itself.
    if (override && projectingPath.length === 0) continue;

    const content = override
      ? (Expressions.getList(override) ?? [])
          .map((expression) => Expressions.getElement(expression))
          .filter((element): element is ExpressionElement => Boolean(element))
      : getSlotDefaultElements(
          null,
          components,
          componentInstance.type,
          slotFieldId
        );
    // Inline slot content is authored alongside its owner, so its path skips
    // the owner the same way page overrides do. Defaults come from the owner.
    const contentPath = override ? projectingPath : instancePath;
    for (const projectedInstance of getOutermostInstances(content)) {
      addInstanceSlotsToMap(components, projectedInstance, map, contentPath);
    }
  }
};

/**
 * If in build mode, returns a map of all page slots and component slots contained
 * in those page slots on a page, with stringified native ID paths (joined with '-')
 * mapped to a boolean.
 *
 * If not in build mode, returns an empty map.
 */
export const getMapOfPageSlotsAndSlotDescendants = singleMemo(
  (isBuildMode: boolean) =>
    weakMemo(
      (
        components: Component.ComponentMap
      ): {
        [key: string]: boolean;
      } => {
        const map: {
          [key: string]: boolean;
        } = {};

        if (!isBuildMode) return map;

        const pageSlots = getAllPageSlots(components);
        if (!pageSlots.length) return map;

        // if we are in build mode, we want to show the empty state for page slots, so add each to the map
        for (const pageSlot of pageSlots) {
          map[pageSlot.id] = true;
          /**
           * Because slots will always be inside components, we can't depend on
           * the element ID of a slot to be unique. Element IDs are unique within components,
           * but not across component instances.
           * Because there can be multiple instances of the same component containing a slot on the page,
           * this means we need to build a unique identifier for each slot.
           * The native ID path of [component-instance-id, slot-id] will be unique,
           * but slot-id itself will not.
           *
           * Therefore, instead of getting all slots from a page slot, which
           * disconnects them from their component instance IDs,
           * we get all instances from a page slot and then all slots from each instance.
           */
          const componentInstancesInPageSlot = findAllDescendantsFromExpression(
            EElement(pageSlot),
            isUserComponentFromElement
          );
          for (const componentInstance of componentInstancesInPageSlot) {
            addInstanceSlotsToMap(components, componentInstance, map);
          }
        }
        return map;
      }
    )
);

/**
 * If in build mode, returns a map of rich text element ids (not inside components)
 * mapped to true. We only want to allow build mode users to add components to
 * rich text elements, not update components with rich text. So we're excluding any
 * rich text inside a component.
 *
 * If not in build mode, returns an empty map.
 */
export const getMapOfRichTextIds = singleMemo((isBuildMode: boolean) =>
  weakMemo((components: Component.ComponentMap): {[key: string]: boolean} => {
    const map: {
      [key: string]: boolean;
    } = {};

    if (!isBuildMode) return map;

    const pageRender = getPageComponentRender(components);
    if (!pageRender) {
      return map;
    }

    const richTextElements = findAllDescendantsFromExpression(
      pageRender,
      isRichText
    );
    if (!richTextElements.length) return map;

    for (const richText of richTextElements) {
      // Don't include rich text elements that are inside components
      if (isUserComponentInst(components, richText)) {
        continue;
      }

      map[richText.id] = true;
    }

    return map;
  })
);

/**
 * If in build mode, returns a map of the page's empty form placeholder element
 * ids mapped to true. The walk covers the page render only, so a placeholder
 * inside a component definition is not mapped. Kept apart from the slot and
 * rich text maps so only the campaign form overlay treats these as buildable.
 *
 * If not in build mode, returns an empty map.
 */
export const getMapOfFormPlaceholderIds = singleMemo((isBuildMode: boolean) =>
  weakMemo((components: Component.ComponentMap): {[key: string]: boolean} => {
    const map: {[key: string]: boolean} = {};

    if (!isBuildMode) return map;

    const pageRender = getPageComponentRender(components);
    if (!pageRender) return map;

    for (const element of findAllDescendantsFromExpression(
      pageRender,
      isEmptyFormPlaceholder
    )) {
      map[element.id] = true;
    }

    return map;
  })
);

function isStaleUserComponent(
  element: ExpressionElement,
  components: Component.ComponentMap,
  staleComponents: {[key: string]: boolean}
) {
  return (
    isUserComponentInst(components, element) &&
    staleComponents[getName(element.type)]
  );
}

/**
 * Returns true if the component or any of its children is a stale component
 * @param component
 * @param stagedComponentMap
 * @param staleComponents
 */
export function isOrContainsStaleUserComponent(
  component: ExpressionElement,
  stagedComponentMap: Component.ComponentMap,
  staleComponents: {[key: string]: boolean}
): boolean {
  if (isStaleUserComponent(component, stagedComponentMap, staleComponents)) {
    return true;
  }
  const descendants = getDescendants(component);
  return descendants.some((descendant) => {
    return isOrContainsStaleUserComponent(
      descendant,
      stagedComponentMap,
      staleComponents
    );
  });
}

/**
 * In page build mode, we check the component map if there is only a body and one page slot,
 * and if this page slot is empty, we can use it to apply a special style class
 */
export const isPageWithOnlyBodyAndPageSlot = singleMemo(
  (isPageBuildMode: boolean) =>
    weakMemo((components: Component.ComponentMap) => {
      if (!isPageBuildMode) {
        return false;
      }

      const pageBody = getBody(components);
      const bodyDescendants = pageBody ? getDescendants(pageBody) : [];

      if (bodyDescendants.length !== 1 || !bodyDescendants[0]) {
        return false;
      }

      return (
        isComponentOnlyDropTarget(bodyDescendants[0]) &&
        getDescendants(bodyDescendants[0]).length === 0
      );
    })
);

export const getDropTargetClass = ({
  components,
  isDropTarget,
  isPageBuildMode,
}: {
  components: Component.ComponentMap;
  isDropTarget: boolean;
  isPageBuildMode: boolean;
}): DropTargetClass | null => {
  if (!isPageBuildMode || !isDropTarget) {
    return null;
  }
  const isSinglePageSlot =
    isPageWithOnlyBodyAndPageSlot(isPageBuildMode)(components);

  if (isSinglePageSlot) {
    return DROP_TARGET_SINGLE_CLASS;
  }

  return DROP_TARGET_CLASS;
};
